import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { tokens } from '../../../../../theme/tokens';

export interface StepBannerStep {
  label: string;
}

interface Props {
  steps: StepBannerStep[];
  /** 0-based index of the step in progress. Steps before it render as done. */
  activeStep: number;
  /** One line under the strip telling the advisor what to do on this step. */
  helper: string;
  /** Right-aligned action buttons for the current step. */
  actions: React.ReactNode;
  /** Bottom margin (theme spacing). */
  mb?: number;
}

/**
 * The two-step flow header shared by Client Details (edit → optimizations) and
 * Add Client: a navy strip with numbered steps, then a white row with helper
 * copy and the step's actions. Presentational — callers own the step state.
 */
const StepBanner: React.FC<Props> = ({ steps, activeStep, helper, actions, mb = 1.75 }) => (
  <Box sx={{ mb, border: `1px solid ${tokens.border}`, borderRadius: '9px', overflow: 'hidden' }}>
    <Stack direction="row" alignItems="center" sx={{ bgcolor: tokens.navy, px: '20px', py: '10px' }}>
      {steps.map((s, i) => {
        const done = i < activeStep;
        const active = i === activeStep;
        return (
          <React.Fragment key={s.label}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  flexShrink: 0,
                  bgcolor: done ? tokens.green : active ? '#fff' : '#FFFFFF22',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {done ? (
                  <svg width="10" height="10" viewBox="0 0 12 12">
                    <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" fill="none" />
                  </svg>
                ) : (
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: active ? tokens.navy : '#FFFFFF55' }}>
                    {i + 1}
                  </Typography>
                )}
              </Box>
              {/* Active step label is WHITE on the navy strip; green only once done (mockup). */}
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: active ? 600 : 400,
                  color: done ? tokens.green : active ? '#fff' : '#FFFFFF55',
                  letterSpacing: 0.1,
                }}
              >
                {s.label}
              </Typography>
            </Stack>
            {i < steps.length - 1 && (
              <Box sx={{ width: 40, height: '1px', bgcolor: '#FFFFFF22', mx: 1.5, flexShrink: 0 }} />
            )}
          </React.Fragment>
        );
      })}
    </Stack>
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={1.5}
      sx={{ bgcolor: '#fff', px: '20px', py: '13px' }}
    >
      <Typography sx={{ fontSize: 12.5, color: tokens.textSec }}>{helper}</Typography>
      <Stack direction="row" gap={1} flexWrap="wrap" flexShrink={0}>
        {actions}
      </Stack>
    </Stack>
  </Box>
);

export default StepBanner;

/* ── Button chrome used inside the banner, so every flow's actions match ── */

/** Quiet secondary action (Cancel, ← Back). */
export const bannerQuietButtonSx = {
  px: 1.75,
  color: tokens.textSec,
  border: `1px solid ${tokens.border}`,
  borderRadius: '7px',
  fontSize: 12.5,
  textTransform: 'none',
} as const;

/** Outlined navy alternative (Save only, Save without running). */
export const bannerOutlineButtonSx = {
  px: 1.75,
  color: tokens.navy,
  border: `1.5px solid ${tokens.navy}`,
  borderRadius: '7px',
  fontSize: 12.5,
  fontWeight: 600,
  textTransform: 'none',
} as const;

/** Green primary — advances the flow (Save & continue, Continue). */
export const bannerAdvanceButtonSx = {
  px: 2,
  bgcolor: tokens.green,
  '&:hover': { bgcolor: '#0a6e57' },
  borderRadius: '7px',
  fontSize: 12.5,
  fontWeight: 700,
  textTransform: 'none',
} as const;

/** Navy primary — runs the plan; greys out while the run gate is closed. */
export const bannerRunButtonSx = (enabled: boolean) =>
  ({
    px: 2.25,
    bgcolor: enabled ? tokens.navy : '#E5E7EB',
    color: enabled ? '#fff' : tokens.textMut,
    '&:hover': { bgcolor: enabled ? '#15305a' : '#E5E7EB' },
    borderRadius: '7px',
    fontSize: 12.5,
    fontWeight: 700,
    textTransform: 'none',
  }) as const;
